import { Fragment } from 'react';
import clsx from 'clsx';
import { useAnalysisSource } from '../components/financial/AnalysisSourceContext';
import type {
  WhatIfScenario,
  WhatIfOverrides,
  WhatIfConfig,
  WhatIfScenarioResult,
} from '../types/financial-model';
import {
  getForecastContext,
  formatQuarter,
  WHAT_IF_SCALE_VARIABLES,
  WHAT_IF_SETTING_VARIABLES,
} from '../types/financial-model';

function formatNumber(value: number): string {
  if (Math.abs(value) >= 1_000) return value.toLocaleString('en-US', { maximumFractionDigits: 0 });
  return value.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

function formatPercent(value: number): string {
  return (value * 100).toFixed(1) + '%';
}

function formatDelta(scenario: number, base: number): string {
  const delta = scenario - base;
  const sign = delta >= 0 ? '+' : '';
  return `${sign}${formatNumber(delta)}`;
}

function deltaColor(scenario: number, base: number, higherIsBetter = true): string {
  const delta = scenario - base;
  if (delta === 0) return 'text-gray-400';
  if (higherIsBetter) return delta > 0 ? 'text-green-600' : 'text-red-600';
  return delta < 0 ? 'text-green-600' : 'text-red-600';
}

function ScenarioCard({
  scenario,
  index,
  onUpdate,
  onRemove,
}: {
  scenario: WhatIfScenario;
  index: number;
  onUpdate: (index: number, updated: WhatIfScenario) => void;
  onRemove: (index: number) => void;
}) {
  const updateOverride = <K extends keyof WhatIfOverrides>(key: K, value: WhatIfOverrides[K]) => {
    onUpdate(index, {
      ...scenario,
      overrides: { ...scenario.overrides, [key]: value },
    });
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white">
      <div className="flex items-center justify-between mb-3">
        <input
          type="text"
          value={scenario.name}
          onChange={(e) => onUpdate(index, { ...scenario, name: e.target.value })}
          className="text-sm font-medium text-gray-900 border border-gray-200 rounded px-2 py-1 w-48 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
        <button
          onClick={() => onRemove(index)}
          className="text-xs text-red-500 hover:text-red-700 px-2 py-1 rounded hover:bg-red-50"
        >
          Remove
        </button>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-3">
        {WHAT_IF_SCALE_VARIABLES.map(({ key, label }) => (
          <div key={key}>
            <label className="block text-xs text-gray-500 mb-1">{label} Scale</label>
            <div className="flex items-center gap-1">
              <input
                type="number"
                step="0.05"
                value={scenario.overrides[key]}
                onChange={(e) => updateOverride(key, parseFloat(e.target.value) || 1)}
                className="w-full text-xs border border-gray-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <span className="text-xs text-gray-400">&times;</span>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-3 gap-3">
        {WHAT_IF_SETTING_VARIABLES.map(({ key, label }) => {
          const isActive = scenario.overrides[key] !== null;
          return (
            <div key={key}>
              <label className="flex items-center gap-1 text-xs text-gray-500 mb-1">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => {
                    if (e.target.checked) {
                      const defaults: Record<string, number> = {
                        cogsRatio: 0.65, interestRate: 0.08, taxRate: 0.20,
                        staffWelfareRate: 0.10, fxRateAED: 3.67,
                      };
                      updateOverride(key, defaults[key] ?? 0);
                    } else {
                      updateOverride(key, null);
                    }
                  }}
                  className="rounded text-blue-600"
                />
                {label}
              </label>
              {isActive && (
                <input
                  type="number"
                  step="0.01"
                  value={scenario.overrides[key] ?? 0}
                  onChange={(e) => updateOverride(key, parseFloat(e.target.value) || 0)}
                  className="w-full text-xs border border-gray-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const RESULT_ROWS: { key: keyof import('../types/financial-model').AnnualSummaryYear; label: string; isPercent?: boolean; higherIsBetter?: boolean }[] = [
  { key: 'totalRevenue', label: 'Revenue' },
  { key: 'grossProfit', label: 'Gross Profit' },
  { key: 'ebitda', label: 'EBITDA' },
  { key: 'profitAfterTax', label: 'PAT' },
  { key: 'grossMargin', label: 'Gross Margin', isPercent: true },
  { key: 'netMargin', label: 'Net Margin', isPercent: true },
];

function ResultsTable({
  baseCase,
  scenarios,
  fiscalYears,
  lastQLabel,
}: {
  baseCase: WhatIfScenarioResult;
  scenarios: WhatIfScenarioResult[];
  fiscalYears: number[];
  lastQLabel: string;
}) {
  const allCases = [baseCase, ...scenarios];

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-gray-200 bg-gray-50">
            <th className="px-3 py-2 text-left font-medium text-gray-500 sticky left-0 bg-gray-50 min-w-[140px]">Metric</th>
            {allCases.map((c, i) => (
              <th key={i} className="px-3 py-2 text-right font-medium text-gray-500 min-w-[120px]">
                {c.scenarioName}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {fiscalYears.map((year) => (
            <Fragment key={`yr-${year}`}>
              <tr className="bg-gray-50">
                <td colSpan={allCases.length + 1} className="px-3 py-1.5 text-xs font-semibold text-gray-700 sticky left-0 bg-gray-50">
                  FY {year}
                </td>
              </tr>
              {RESULT_ROWS.map((row) => (
                <tr key={`${year}-${row.key}`} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-3 py-1.5 text-gray-600 sticky left-0 bg-white">{row.label}</td>
                  {allCases.map((c, i) => {
                    const yearData = c.annualSummary[year];
                    if (!yearData) return <td key={i} className="px-3 py-1.5 text-right text-gray-400">-</td>;
                    const val = yearData[row.key];
                    const baseVal = baseCase.annualSummary[year]?.[row.key] ?? 0;
                    const isBase = i === 0;

                    return (
                      <td key={i} className="px-3 py-1.5 text-right">
                        <span className="text-gray-900">
                          {row.isPercent ? formatPercent(val) : formatNumber(val)}
                        </span>
                        {!isBase && (
                          <span className={`block text-[10px] ${deltaColor(val, baseVal, row.higherIsBetter !== false)}`}>
                            {row.isPercent
                              ? `${val - baseVal >= 0 ? '+' : ''}${((val - baseVal) * 100).toFixed(1)}pp`
                              : formatDelta(val, baseVal)}
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </Fragment>
          ))}

          {/* Terminal metrics */}
          <tr className="bg-gray-50">
            <td colSpan={allCases.length + 1} className="px-3 py-1.5 text-xs font-semibold text-gray-700 sticky left-0 bg-gray-50">
              Terminal ({lastQLabel})
            </td>
          </tr>
          <tr className="border-b border-gray-100 hover:bg-gray-50">
            <td className="px-3 py-1.5 text-gray-600 sticky left-0 bg-white">Closing Cash</td>
            {allCases.map((c, i) => (
              <td key={i} className="px-3 py-1.5 text-right">
                <span className="text-gray-900">{formatNumber(c.closingCash)}</span>
                {i > 0 && (
                  <span className={`block text-[10px] ${deltaColor(c.closingCash, baseCase.closingCash)}`}>
                    {formatDelta(c.closingCash, baseCase.closingCash)}
                  </span>
                )}
              </td>
            ))}
          </tr>
          <tr className="border-b border-gray-100 hover:bg-gray-50">
            <td className="px-3 py-1.5 text-gray-600 sticky left-0 bg-white">Net Worth</td>
            {allCases.map((c, i) => (
              <td key={i} className="px-3 py-1.5 text-right">
                <span className="text-gray-900">{formatNumber(c.netWorth)}</span>
                {i > 0 && (
                  <span className={`block text-[10px] ${deltaColor(c.netWorth, baseCase.netWorth)}`}>
                    {formatDelta(c.netWorth, baseCase.netWorth)}
                  </span>
                )}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export function WhatIfView() {
  // Sourced through the context so the same view serves v1 and v2. With no
  // provider it reads the v1 store, which is what v1 does today.
  const {
    settings,
    whatIf,
    isRecalculating,
    isDirty,
    updateSetting,
    recalculate: recalculateAndSave,
  } = useAnalysisSource();

  if (!settings || !whatIf) return null;

  const config = settings.whatIfConfig;
  const ctx = getForecastContext(settings);
  const lastQLabel = formatQuarter(ctx.lastQuarter);

  const updateConfig = (newConfig: WhatIfConfig) => {
    updateSetting('whatIfConfig', newConfig);
  };

  const handleUpdateScenario = (index: number, updated: WhatIfScenario) => {
    const newScenarios = [...config.scenarios];
    newScenarios[index] = updated;
    updateConfig({ scenarios: newScenarios });
  };

  const handleRemoveScenario = (index: number) => {
    updateConfig({ scenarios: config.scenarios.filter((_, i) => i !== index) });
  };

  const handleAddScenario = () => {
    updateConfig({
      scenarios: [
        ...config.scenarios,
        {
          name: `Scenario ${config.scenarios.length + 1}`,
          overrides: {
            revenueScaleFactor: 1.0,
            headcountScaleFactor: 1.0,
            opexScaleFactor: 1.0,
            cogsRatio: null,
            interestRate: null,
            taxRate: null,
            staffWelfareRate: null,
            fxRateAED: null,
          },
        },
      ],
    });
  };

  return (
    <div className="h-full min-w-0 overflow-auto p-6 space-y-6">
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">What-If Scenarios</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Define named scenarios with multiple variable changes for side-by-side comparison.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleAddScenario}
              className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 flex items-center gap-1"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
              </svg>
              Add Scenario
            </button>
            <button
              onClick={() => void recalculateAndSave()}
              disabled={isRecalculating || !isDirty}
              className={clsx(
                'px-4 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center gap-1.5',
                isDirty && !isRecalculating
                  ? 'text-white bg-green-600 hover:bg-green-700'
                  : 'text-gray-400 bg-gray-100 cursor-not-allowed',
              )}
            >
              {isRecalculating ? (
                <>
                  <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Running...
                </>
              ) : (
                <>
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 0 1 0 1.972l-11.54 6.347a1.125 1.125 0 0 1-1.667-.986V5.653Z" />
                  </svg>
                  Run
                </>
              )}
            </button>
          </div>
        </div>

        {config.scenarios.length === 0 ? (
          <div className="text-center py-8 bg-white rounded-lg border border-gray-200">
            <p className="text-sm text-gray-500">No scenarios defined. Add one to get started.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            {config.scenarios.map((scenario, i) => (
              <ScenarioCard
                key={i}
                scenario={scenario}
                index={i}
                onUpdate={handleUpdateScenario}
                onRemove={handleRemoveScenario}
              />
            ))}
          </div>
        )}
      </div>

      <div className={isRecalculating ? 'opacity-50 transition-opacity' : ''}>
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Results Comparison</h3>
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <ResultsTable baseCase={whatIf.baseCase} scenarios={whatIf.scenarios} fiscalYears={ctx.fiscalYears} lastQLabel={lastQLabel} />
        </div>
      </div>
    </div>
  );
}
